import React from 'react';
import { MapPin, UserRound, X } from 'lucide-react';
import { LanguageFlag } from './LanguageFlag';
import { SESSION_LANGUAGE_OPTIONS, timezoneShortLabel } from './sessionFormOptions';
import type { ProgramSlotDraft } from './ProgramSessionsCalendar';

type Props = {
  slots: ProgramSlotDraft[];
  sessionCodes?: Record<number, string | null | undefined>;
  selectedIndex?: number | null;
  compact?: boolean;
  isException?: (slot: ProgramSlotDraft) => boolean;
  onRemove: (index: number) => void;
  onSelect?: (index: number) => void;
};

const formatDate = (scheduledAt: string, timezone: string) => {
  if (!scheduledAt) return '—';
  return new Date(scheduledAt).toLocaleDateString('fr-FR', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: timezone || undefined,
  });
};

const formatTime = (scheduledAt: string) => {
  if (!scheduledAt || scheduledAt.length < 16) return '—';
  return scheduledAt.slice(11, 16);
};

export const ProgramSessionsList: React.FC<Props> = ({
  slots,
  sessionCodes = {},
  selectedIndex = null,
  compact = false,
  isException,
  onRemove,
  onSelect,
}) => {
  const scheduled = slots
    .map((slot, index) => ({ slot, index }))
    .filter(({ slot }) => slot.scheduledAt);

  if (scheduled.length === 0) {
    return (
      <div className="rounded-2xl border border-dashed border-indigo-200 bg-indigo-50/30 px-6 py-10 text-center">
        <p className="text-sm font-semibold text-slate-600">Aucune séance planifiée</p>
        <p className="mt-1 text-xs text-slate-500">Passez en vue Calendrier et cliquez un jour pour commencer.</p>
      </div>
    );
  }

  return (
    <div className={compact ? 'max-h-[380px] overflow-y-auto pr-1' : undefined}>
      <div className={`mb-2 hidden items-center gap-2 px-1 font-bold uppercase tracking-wider text-slate-400 sm:flex ${compact ? 'text-[8px]' : 'text-[9px]'}`}>
        <span className="w-6" />
        <span className="flex-1">Date</span>
        <span className="w-14">Heure</span>
        <span className="w-10">Langue</span>
        {!compact && <span className="w-28">Lieu · Animateur</span>}
        <span className="w-[74px]">Code</span>
        <span className="w-6" />
      </div>

      <div className={compact ? 'flex flex-col gap-1.5' : 'flex flex-col gap-2'}>
        {scheduled.map(({ slot, index }, displayIndex) => {
          const lang = SESSION_LANGUAGE_OPTIONS.find((opt) => opt.value === slot.language);
          const code = slot.slotId ? sessionCodes[slot.slotId] : undefined;

          const isSelected = selectedIndex === index;

          return (
            <div
              key={slot.slotId ?? `new-${index}`}
              role={onSelect ? 'button' : undefined}
              tabIndex={onSelect ? 0 : undefined}
              onClick={() => onSelect?.(index)}
              onKeyDown={(e) => {
                if (onSelect && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onSelect(index);
                }
              }}
              className={[
                'flex items-center gap-2 rounded-xl border px-3 sm:gap-3',
                compact ? 'py-2' : 'py-3 sm:px-3.5',
                isSelected
                  ? 'border-indigo-300 bg-indigo-50 ring-2 ring-indigo-200'
                  : 'border-slate-200 bg-white',
                onSelect ? 'cursor-pointer hover:border-indigo-200 hover:bg-indigo-50/40' : '',
              ].join(' ')}
            >
              <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-indigo-100 text-[11px] font-black text-indigo-700">
                {displayIndex + 1}
              </span>

              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold capitalize text-slate-900">
                  {formatDate(slot.scheduledAt, slot.timezone)}
                </span>
                {isException?.(slot) && (
                  <span className="mt-0.5 inline-block rounded-full bg-amber-100 px-2 py-px text-[10px] font-bold text-amber-700">
                    exception
                  </span>
                )}
              </span>

              <span className="hidden w-14 shrink-0 font-mono text-sm font-semibold text-slate-800 sm:block">
                {formatTime(slot.scheduledAt)}
              </span>

              <span className="hidden w-10 shrink-0 items-center gap-1 sm:flex">
                {lang ? (
                  <LanguageFlag countryCode={lang.countryCode} title={lang.label} className="h-3 w-4 rounded-sm" />
                ) : (
                  <span className="text-xs text-slate-400">—</span>
                )}
              </span>

              {!compact && (
                <span className="hidden w-28 shrink-0 flex-col gap-0.5 text-xs text-slate-500 sm:flex">
                  <span className="inline-flex items-center gap-1 truncate">
                    <MapPin className="h-3 w-3 shrink-0 text-slate-400" />
                    <span className="truncate">
                      {slot.location || '—'}
                      {slot.timezone ? ` · ${timezoneShortLabel(slot.timezone)}` : ''}
                    </span>
                  </span>
                  <span className="inline-flex items-center gap-1 truncate">
                    <UserRound className="h-3 w-3 shrink-0 text-slate-400" />
                    <span className="truncate">{slot.facilitator || '—'}</span>
                  </span>
                </span>
              )}

              <span className="hidden w-[74px] shrink-0 sm:block">
                {code ? (
                  <span className="inline-block rounded-md bg-indigo-50 px-2 py-1 text-center font-mono text-xs font-bold text-indigo-600">
                    {code}
                  </span>
                ) : (
                  <span className="text-xs text-slate-400">—</span>
                )}
              </span>

              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove(index);
                }}
                className="ml-auto shrink-0 rounded-lg p-1 text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600"
                aria-label="Retirer la séance"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
};
